/**
 * SeamlessTravel — a portal trip that keeps the page (plan 25, D36).
 *
 * A portal used to be a full page load: every map paid Pixi renderer start-up,
 * the first shader compiles, the React mount and the atmosphere bakes again
 * (plan 21 §10.3). This trip composes three paths that already exist instead:
 *
 *   1. the destination URL becomes the page's boot URL (pushBootUrl), so every
 *      boot reader — and a refresh at any later moment — names the destination;
 *   2. the scene restart (GameScene.shutdown → kernel.shutdown, then a new
 *      GameScene) rebuilds the world on the same Pixi app;
 *   3. the fresh-rejoin path: a navigating, consented leave → `net:disconnected`
 *      → Game.tsx's connect effect joins again with the boot URL's map, spawn and
 *      receipt (the wire is unchanged), and NetworkSyncSystem rebinds on connect.
 *
 * Anything that goes wrong falls back to reloading the (already destination)
 * URL, which is exactly the old behaviour. Behind isSeamlessPortalsEnabled().
 */
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { gameLogger } from '../../../utils/GameLogger';
import { pushBootUrl } from '../../../config/boot/bootUrl';
import type { MapTravelTarget } from './mapTravelUrl';

/** The world half of a trip, registered by Game.tsx (it owns the GameHost). */
export interface TravelSceneControls {
  /** Stop the active GameScene (kernel shutdown). */
  stop(): void;
  /** Start GameScene on `levelId`; resolves once its create() finished. */
  start(target: { levelId: string; spawnId?: string }): Promise<void>;
}

interface LeavableRoom {
  sessionId?: string;
  leave(consented?: boolean): Promise<unknown>;
}

export interface SeamlessTravelDeps {
  bus: Pick<EventBus, 'emit' | 'on'>;
  getRoom(): LeavableRoom | null;
  markNavigating(): void;
  flushVfx(): void;
  pushUrl(url: string): void;
  /** Last resort: reload the current (destination) entry. */
  reload(): void;
  installBackReload(): void;
  leaveTimeoutMs: number;
  joinTimeoutMs: number;
}

export class SeamlessTravel {
  private controls: TravelSceneControls | null = null;
  private inFlight = false;

  constructor(private readonly deps: SeamlessTravelDeps) {}

  /** Game.tsx registers the scene controls; returns the unregister. */
  registerScene(controls: TravelSceneControls): () => void {
    this.controls = controls;
    return () => { if (this.controls === controls) this.controls = null; };
  }

  /**
   * Back reloads the popped entry, as when every map was its own page. Armed at
   * boot while the flag is on (a page reloaded after a trip still has the
   * trip's history entries behind it), and again by every trip.
   */
  armBackNavigation(): void {
    this.deps.installBackReload();
  }

  /** True when an in-place trip can run (scene controls are registered). */
  canTravel(): boolean {
    return this.controls !== null && !this.inFlight;
  }

  isTravelling(): boolean {
    return this.inFlight;
  }

  /**
   * Travel to `target`, whose full-page URL is `url`. Resolves true when the
   * destination scene is ready in place, false when it fell back to a reload.
   */
  async travel(url: string, target: MapTravelTarget): Promise<boolean> {
    const controls = this.controls;
    if (!controls || this.inFlight) return false;
    this.inFlight = true;
    const { deps } = this;
    const mapId = target.mapId;
    gameLogger.info('SYSTEM', `🧭 [SeamlessTravel] In-place trip → ${mapId}`);
    deps.bus.emit(EVENTS.MAP_TRAVEL_START, { mapId });

    const oldRoom = deps.getRoom();
    const oldSessionId = oldRoom?.sessionId ?? null;
    // Subscribe before leaving so a fast join cannot slip past us.
    const joined = this.waitForNewSession(oldSessionId);
    try {
      deps.pushUrl(url);
      deps.installBackReload();
      deps.markNavigating();
      deps.flushVfx();
      controls.stop();
      if (oldRoom) await withTimeout(oldRoom.leave(true), deps.leaveTimeoutMs);
      const connected = await joined.promise;
      if (!connected) throw new Error(`no join within ${deps.joinTimeoutMs} ms`);
      await controls.start({ levelId: mapId, spawnId: target.spawnId });
      deps.bus.emit(EVENTS.MAP_TRAVEL_COMPLETE, { mapId, ok: true });
      gameLogger.info('SYSTEM', `✅ [SeamlessTravel] Arrived in ${mapId} without a reload`);
      return true;
    } catch (error) {
      gameLogger.warn('SYSTEM', `[SeamlessTravel] In-place trip to ${mapId} failed — reloading`, error as Error);
      deps.bus.emit(EVENTS.MAP_TRAVEL_COMPLETE, { mapId, ok: false });
      deps.reload();
      return false;
    } finally {
      joined.dispose();
      this.inFlight = false;
    }
  }

  private waitForNewSession(oldSessionId: string | null): { promise: Promise<boolean>; dispose(): void } {
    let unsub: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const promise = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => resolve(false), this.deps.joinTimeoutMs);
      unsub = this.deps.bus.on(EVENTS.NET_CONNECTED, (data) => {
        if (data?.sessionId && data.sessionId !== oldSessionId) resolve(true);
      });
    });
    return {
      promise,
      dispose: () => {
        if (timer) clearTimeout(timer);
        unsub?.();
      },
    };
  }
}

async function withTimeout(work: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    await Promise.race([
      work.catch(() => undefined),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, ms); }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// ── App-wide instance ────────────────────────────────────────────────────────

let backReloadInstalled = false;

type TravelWindow = {
  nm?: { getRoom?: () => LeavableRoom | null; markNavigating?: () => void };
  __PIXI_OVERLAY_HOST__?: { flushVfx?: () => void };
  location: { reload(): void };
  addEventListener(type: 'popstate', listener: () => void): void;
};

function travelWindow(): TravelWindow | null {
  return typeof window === 'undefined' ? null : (window as unknown as TravelWindow);
}

/** The page's single trip runner. */
export const seamlessTravel = new SeamlessTravel({
  bus: EventBus.getInstance(),
  getRoom: () => travelWindow()?.nm?.getRoom?.() ?? null,
  markNavigating: () => travelWindow()?.nm?.markNavigating?.(),
  flushVfx: () => {
    try {
      travelWindow()?.__PIXI_OVERLAY_HOST__?.flushVfx?.();
    } catch (error) {
      gameLogger.warn('renderer', '[SeamlessTravel] Pixi VFX flush failed before the trip', error as Error);
    }
  },
  pushUrl: pushBootUrl,
  reload: () => travelWindow()?.location.reload(),
  // Every history entry used to be its own page, so Back reloaded the
  // previous map. Keep that: a popstate while in game reloads the popped entry.
  installBackReload: () => {
    const w = travelWindow();
    if (!w || backReloadInstalled) return;
    backReloadInstalled = true;
    w.addEventListener('popstate', () => w.location.reload());
  },
  leaveTimeoutMs: 2000,
  joinTimeoutMs: 15000,
});
